import { createContext, useContext, useEffect } from 'react'

/** The header chip: the trial's length before it starts, the days left once it runs. */
export interface TrialState {
  days: number
  running: boolean
}

/** What a wizard page tells the onboarding layout to draw around it. */
export interface OnboardingFrameConfig {
  /** 0 is the welcome screen, 1–9 the steps, 10 the done screen. Without it there is no rail. */
  step?: number
  /** Steps finished, shown done and clickable. Defaults to every step before `step`. */
  done?: readonly number[]
  /** Done steps still to finish: the design's "To do" (verification left for later). */
  todo?: readonly number[]
  /** Opens a done step from the rail. */
  onJump?: (step: number) => void
  trial?: TrialState | null
  /** Shows "Save and exit" in the header. */
  onSaveExit?: () => void
  /** "Save and exit" is saving. */
  saving?: boolean
  /** The plan and payment steps' wider column (`.ob-widecol`). */
  wide?: boolean
}

/** The config as the layout keeps it: data only; the handlers live in a ref. */
export interface OnboardingFrameView extends Omit<OnboardingFrameConfig, 'onJump' | 'onSaveExit'> {
  canJump: boolean
  canSaveExit: boolean
}

export type OnboardingFrameHandlers = Pick<OnboardingFrameConfig, 'onJump' | 'onSaveExit'>

interface OnboardingFrameApi {
  setView: (view: OnboardingFrameView | null) => void
  /** Keeps the latest handlers without re-rendering the layout. */
  setHandlers: (handlers: OnboardingFrameHandlers) => void
}

export const OnboardingFrameContext = createContext<OnboardingFrameApi | null>(null)

const toList = (key: string | undefined) => (key ? key.split(',').map(Number) : key === '' ? [] : undefined)

/**
 * Fills the onboarding layout's frame -- the step rail, the mobile progress bar and
 * the header's trial chip and "Save and exit" -- from the page inside it. Lists and
 * handlers may be new on every render: the layout re-renders only when the data
 * changes, and always calls the latest handler.
 */
export function useOnboardingFrame(config: OnboardingFrameConfig) {
  const api = useContext(OnboardingFrameContext)
  const { step, onJump, onSaveExit, saving, wide } = config
  const done = config.done?.join(',')
  const todo = config.todo?.join(',')
  const trialDays = config.trial?.days
  const trialRunning = config.trial?.running === true
  const canJump = onJump !== undefined
  const canSaveExit = onSaveExit !== undefined

  useEffect(() => {
    api?.setHandlers({ onJump, onSaveExit })
  })
  useEffect(() => {
    api?.setView({
      step,
      done: toList(done),
      todo: toList(todo),
      trial: trialDays === undefined ? null : { days: trialDays, running: trialRunning },
      saving,
      wide,
      canJump,
      canSaveExit,
    })
  }, [api, step, done, todo, trialDays, trialRunning, saving, wide, canJump, canSaveExit])
  useEffect(() => () => api?.setView(null), [api])
}

/**
 * "Save and exit" on a step with answers: the wizard runs the step's own save, as its
 * Continue would, and leaves once the step moves on. A step that stops on something to
 * fix, or whose save is refused, calls `stay`.
 */
export interface StepSaveApi {
  register: (save: (() => void) | null) => void
  stay: () => void
}

export const StepSaveContext = createContext<StepSaveApi | null>(null)

/** Gives "Save and exit" this step's save (none: it saves only where the person is). Returns `stay`. */
export function useStepSave(save: (() => void) | null) {
  const api = useContext(StepSaveContext)
  useEffect(() => {
    if (!api || !save) return
    api.register(save)
    return () => api.register(null)
  })
  return () => api?.stay()
}
